import { FC } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { MinMaxEditor, hasMinMaxErrors, getHumanMinMax } from "./MinMaxEditor";
import type { ScoringType, SpendBand, MinMaxBand } from "@/lib/scoring/types";
import { useTaxonomyContext } from "@/contexts/Taxonomy/TaxonomyContext";
import { SpendCategoryAutocomplete } from "@/components/filters/SpendCategory/SpendCategoryAutocomplete";

export const initialBand: SpendBand = { score: 0 };

export const hasErrors = (bands: unknown[]): boolean => {
  const spendBands = bands as SpendBand[];
  if (spendBands.length === 0) return false;
  if (!spendBands[0]?.categoryId) return true;
  const minMaxBands: MinMaxBand[] = spendBands.map((b) => ({
    min: b.min,
    max: b.max,
    score: b.score,
  }));
  return hasMinMaxErrors(minMaxBands);
};

export const getBandSummary = (band: unknown): string => {
  const b = band as SpendBand;
  return getHumanMinMax(b.min, b.max, "$");
};

interface Props {
  criterion: ScoringType;
  onChange: (updated: ScoringType) => void;
}

export const ITSpendEditor: FC<Props> = ({ criterion, onChange }) => {
  const { spendCategories } = useTaxonomyContext();
  const bands = criterion.spend ?? [];
  const categoryId = bands[0]?.categoryId;

  const showCategoryError = bands.length > 0 && !categoryId;

  const handleCategoryChange = (id: string | undefined) => {
    onChange({
      ...criterion,
      spend: bands.map((b) => ({ ...b, categoryId: id })),
    });
  };

  const handleBandsChange = (minMaxBands: MinMaxBand[]) => {
    onChange({
      ...criterion,
      spend: minMaxBands.map((b) => ({ ...b, categoryId })),
    });
  };

  return (
    <Box>
      <Box sx={{ px: 2, mb: 2 }}>
        <SpendCategoryAutocomplete
          categories={spendCategories}
          value={categoryId}
          onChange={handleCategoryChange}
          label="Spend category"
          required
          error={showCategoryError}
          helperText={
            showCategoryError
              ? "You must select an IT Spend Category"
              : undefined
          }
          testId="scoring-spend-category-input"
        />
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ mt: 0.5, display: "block" }}
        >
          Category applies to all bands
        </Typography>
      </Box>
      <MinMaxEditor
        bands={bands.map((b) => ({ min: b.min, max: b.max, score: b.score }))}
        onChange={handleBandsChange}
        minLabel="Min Amount"
        maxLabel="Max Amount"
        thousandSeparator
        prefix="$"
        minInputTestId="scoring-min-amount-input"
        maxInputTestId="scoring-max-amount-input"
      />
    </Box>
  );
};
